import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { FunctionComponent } from "react";
import { useEffect, useMemo, useState } from "react";

import { INVOICES_INDEX_PAGE_STATE_KEY } from "~/constants/pageStateStorageKeys.ts";
import usePageStateStorage from "~/hooks/usePageStateStorage";
import userViewGuard from "~/hooks/useViewPermssionsCheck";
import { invoiceStatusQueries } from "~/modules/billing/api/invoiceStatus/invoiceStatusQueries.ts";
import { processedInvoiceQueries } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type { InvoicesIndexTableData } from "~/modules/billing/components/InvoicesIndexView/InvoicesIndexTableTypes.ts";
import { invoicesIndexDefaultPageState } from "~/modules/billing/components/InvoicesIndexView/invoicesIndexUtils.tsx";
import InvoicesIndexView from "~/modules/billing/components/InvoicesIndexView/InvoicesIndexView.tsx";
import type { DateRange } from "~/types/dateAndTimeTypes.ts";
import { PermissionNames } from "~/types/entityNames.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";

const InvoicesIndexPage: FunctionComponent = () => {
	const guard = userViewGuard([PermissionNames.CanManageInvoices, PermissionNames.CanViewInvoices]);

	if (guard) return guard;
	const [showOverdueOnly, setShowOverdueOnly] = useState<boolean>(false);
	const {
		pageState: { deliveryPeriodFilter: deliveryPeriodFilterFromPageState },
		setPageState,
	} = usePageStateStorage({
		pageKey: INVOICES_INDEX_PAGE_STATE_KEY,
		defaultState: invoicesIndexDefaultPageState,
	});

	const [deliveryPeriodFilter, setDeliveryPeriodFilter] = useState<DateRange>({
		startDate: new Date(deliveryPeriodFilterFromPageState.startDate),
		endDate: new Date(deliveryPeriodFilterFromPageState.endDate),
	});


	useEffect(() => {
		setPageState((prevState) => ({
			...prevState,
			deliveryPeriodFilter: {
				startDate: deliveryPeriodFilter.startDate.toISOString(),
				endDate: deliveryPeriodFilter.endDate.toISOString(),
			},
		}));
	}, [deliveryPeriodFilter.endDate, deliveryPeriodFilter.startDate, setPageState]);

	const {
		data: invoices,
		isLoading: invoicesIsLoading,
		isFetching: invoicesIsFetching,
	} = useQuery({
		...processedInvoiceQueries.inPeriod({
			dateFrom: formatDateToYYYYMMDD(deliveryPeriodFilter.startDate),
			dateTo: formatDateToYYYYMMDD(deliveryPeriodFilter.endDate),
			includeAllOverdue: showOverdueOnly,
		}), placeholderData: keepPreviousData,
	});

	const {
		data: statuses,
		isLoading: statusesIsLoading,
	} = useQuery(invoiceStatusQueries.all());

	const tableData: InvoicesIndexTableData[] = useMemo(() => {
		if (invoices && statuses) {
			const statusIdToOrderMap = new Map(statuses.map((status, index) => [status.id, index]));
			return invoices
				.map((invoice) => {
					const transactionDate = invoice.sentAt || invoice.receivedAt;
					const isOverdue = invoice.paymentDueAt && invoice.paidAt === null ? new Date() > new Date(invoice.paymentDueAt) : false;
					return {
						...invoice,
						deliveryEndDate: new Date(invoice.deliveryEndDate),
						deliveryStartDate: new Date(invoice.deliveryStartDate),
						invoiceStatusOrder: statusIdToOrderMap.get(invoice.invoiceStatusId) ?? Number.MAX_SAFE_INTEGER,
						isOverdue,
						paidAt: invoice.paidAt ? new Date(invoice.paidAt) : null,
						paymentDueAt: invoice.paymentDueAt ? new Date(invoice.paymentDueAt) : null,
						sentAt: invoice.sentAt ? new Date(invoice.sentAt) : null,
						transactionDate: transactionDate ? new Date(transactionDate) : null,
						updatedAt: new Date(invoice.updatedAt),
					};
				});

		}
		return [];
	}, [invoices, statuses]);

	return <InvoicesIndexView
		deliveryPeriodFilter={deliveryPeriodFilter}
		isFetching={invoicesIsFetching}
		isLoading={invoicesIsLoading || statusesIsLoading}
		invoiceStatuses={statuses || []}
		setDeliveryPeriodFilter={setDeliveryPeriodFilter}
		setShowOverdueOnly={setShowOverdueOnly}
		showOverdueOnly={showOverdueOnly}
		tableData={tableData} />;
};

export default InvoicesIndexPage;